import { useCallback, useMemo } from "react"
import { useGraphState } from 'uikit/graphs/default'
import type {
    ApiGraphData,
    NetworkNode,
    NetworkEdge,
    NetworkNodeData,
    NetworkEdgeData,
    UseGraphModelReturn,
    UseGraphModelOptions
} from "../types"
import { transformApiToReactFlow } from "../utils/transform-api-data"
import { transformReactFlowToApi } from "../utils/transform-to-api"

/**
 * Hook for managing graph data model.
 * Handles API data transformation, node/edge enrichment, and domain state.
 */
export function useGraphModel(options: UseGraphModelOptions = {}): UseGraphModelReturn {
    const { initialData, onChange, readonlyNodeTypes } = options

    const initialTransformed = useMemo(() => {
        if (!initialData) return { nodes: [], edges: [] }
        return transformApiToReactFlow(initialData)
    }, [initialData])

    const graphState = useGraphState<NetworkNodeData, NetworkEdgeData>({
        initialNodes: initialTransformed.nodes,
        initialEdges: initialTransformed.edges,
        onChange: (nodes, edges) => {
            if (onChange) {
                const apiData = transformReactFlowToApi(nodes as NetworkNode[], edges as NetworkEdge[])
                onChange(apiData)
            }
        }
    })

    const disabledNodeIds = useMemo(() => {
        return graphState.nodes.filter((n) => n.data.disabled).map((n) => n.id)
    }, [graphState.nodes])

    // Add computed attributes to nodes (e.g., readonly based on node type)
    const enrichedNodes = useMemo(() => {
        const readonlyTypes = new Set(readonlyNodeTypes ?? [])
        if (readonlyTypes.size === 0) return graphState.nodes as NetworkNode[]

        return graphState.nodes.map((n) => {
            const data = n.data
            if (readonlyTypes.has(data.nodeType) && !data.readonly) {
                return { ...n, data: { ...data, readonly: true } }
            }
            return n
        }) as NetworkNode[]
    }, [graphState.nodes, readonlyNodeTypes])

    const readonlyNodeIdsSet = useMemo(() => {
        return new Set(enrichedNodes.filter((n) => n.data.readonly).map((n) => n.id))
    }, [enrichedNodes])

    // Add computed attributes to edges based on connected node states
    // Note: markerEnd is handled by BaseEdge component to support state-based coloring (selected, disabled, etc.)
    const enrichedEdges = useMemo(() => {
        const disabledSet = new Set(disabledNodeIds)
        return graphState.edges.map((edge) => {
            const isDisabled = disabledSet.has(edge.source) || disabledSet.has(edge.target)
            const isReadonly = readonlyNodeIdsSet.has(edge.source) || readonlyNodeIdsSet.has(edge.target)
            return {
                ...edge,
                data: {
                    ...edge.data,
                    disabled: isDisabled,
                    readonly: isReadonly
                }
            }
        }) as NetworkEdge[]
    }, [graphState.edges, disabledNodeIds, readonlyNodeIdsSet])

    const toggleNodeDisabled = useCallback(
        (nodeId: string) => {
            if (readonlyNodeIdsSet.has(nodeId)) return

            const node = graphState.nodes.find((n) => n.id === nodeId)
            if (!node) return

            graphState.updateNode(nodeId, { disabled: !node.data.disabled })
        },
        [graphState, readonlyNodeIdsSet]
    )

    const getApiData = useCallback((): ApiGraphData => {
        return transformReactFlowToApi(enrichedNodes, enrichedEdges)
    }, [enrichedNodes, enrichedEdges])

    return {
        nodes: enrichedNodes,
        edges: enrichedEdges,
        onNodesChange: graphState.onNodesChange,
        onEdgesChange: graphState.onEdgesChange,
        toggleNodeDisabled,
        getApiData,
        addNode: graphState.addNode,
        addEdge: graphState.addEdge,
        updateNode: graphState.updateNode,
        updateEdge: graphState.updateEdge,
        removeNode: graphState.removeNode,
        removeEdge: graphState.removeEdge
    }
}
